import { type FC, type KeyboardEvent, useMemo } from 'react';
import { RiArrowRightSLine } from 'react-icons/ri';
import Button from '@/components/UI/Button/Button';
import { _isNotBlank } from '@/littledash';
import { ModalContainer, ModalHeader } from '@/utils/modal';
import {
  MAX_WORKFLOW_TEMPLATES_PER_STUDY,
  sortTemplatesByRecentUse,
  type WorkflowTemplateSummary,
} from './WorkflowTemplatePicker.utils';
import styles from './WorkflowTemplatePicker.module.scss';

const TEST_COMPONENT = 'WorkflowTemplatePicker';

interface WorkflowTemplatePickerProps {
  templates: Array<WorkflowTemplateSummary>;
  onStartFromScratch: () => void;
  onUseTemplate: (template: WorkflowTemplateSummary) => void;
  onClose: () => void;
  testId?: string;
}

/**
 * First step of the workflow setup when the study has saved templates: start a blank setup, or
 * start from one of the study's templates, most recently used first.
 */
const WorkflowTemplatePicker: FC<WorkflowTemplatePickerProps> = ({
  templates,
  onStartFromScratch,
  onUseTemplate,
  onClose,
  testId = 'workflow-template-picker',
}) => {
  const sortedTemplates = useMemo(() => sortTemplatesByRecentUse(templates), [templates]);
  const templateLimitReached = templates.length >= MAX_WORKFLOW_TEMPLATES_PER_STUDY;

  const startFromScratchOnKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onStartFromScratch();
    }
  };

  return (
    <ModalContainer size="medium">
      <div data-testid={testId} data-test-component={TEST_COMPONENT} data-test-element="container">
        <ModalHeader title="Workflow setup" fontClass="basier-med normal f4 lh-title near-black" closeModal={onClose} />
        <div className="pa3">
          <div
            role="button"
            tabIndex={0}
            className={styles.startFromScratch}
            onClick={onStartFromScratch}
            onKeyDown={startFromScratchOnKey}
            data-test-component={TEST_COMPONENT}
            data-test-element="start-from-scratch"
          >
            <div>
              <span className={styles.startFromScratchTitle}>Start from scratch</span>
              <span className={styles.startFromScratchSubtitle}>Configure every setting manually</span>
            </div>
            <RiArrowRightSLine size={24} aria-hidden />
          </div>
          <div className="flex justify-between items-center mt4 mb3">
            <h4 className={styles.sectionTitle}>Most recent</h4>
            <span
              className={`${styles.templateCount} ${templateLimitReached ? styles.templateCountFull : ''}`}
              data-test-component={TEST_COMPONENT}
              data-test-element="count"
            >
              {templates.length}/{MAX_WORKFLOW_TEMPLATES_PER_STUDY} templates
            </span>
          </div>
          <ul className={styles.templates} data-test-component={TEST_COMPONENT} data-test-element="templates">
            {sortedTemplates.map((template) => (
              <li
                key={template.id}
                className={styles.template}
                data-test-component={TEST_COMPONENT}
                data-test-element="template"
                data-test-key={template.id}
              >
                <div className={styles.templateText}>
                  <p className={styles.templateName} data-test-component={TEST_COMPONENT} data-test-element="name">
                    {template.name}
                  </p>
                  {_isNotBlank(template.description) && (
                    <p
                      className={styles.templateDescription}
                      data-test-component={TEST_COMPONENT}
                      data-test-element="description"
                    >
                      {template.description}
                    </p>
                  )}
                </div>
                <Button plain onClick={() => onUseTemplate(template)} testKey={`use-template-${template.id}`}>
                  Use template
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ModalContainer>
  );
};

export default WorkflowTemplatePicker;
